import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

/** Clockwise perimeter of the 4×4 grid, corners excluded. */
const ring = [1, 2, 7, 11, 14, 13, 8, 4];
const corners = new Set([0, 3, 12, 15]);

const dots = Array.from({ length: 16 }, (_, index) => {
  if (corners.has(index)) {
    return { index, role: 'gap' } as const;
  }

  const step = ring.indexOf(index);

  return step === -1
    ? ({ index, role: 'hold' } as const)
    : ({ index, role: 'ring', step } as const);
});

type Props = {
  /** Small sits in a 16px button icon slot; medium is the loading tile's glyph. */
  readonly size?: 'sm' | 'md';
  readonly className?: string;
};

const ringDelay = (step: number): CSSProperties & { '--d': number } => ({ '--d': step });

/**
 * The transitions.dev matrix dot loader, orbit variant: a lit dot circles the ring while the centre
 * holds. The markup is mirrored in index.html's pre-boot tile, so keep the two in step.
 */
const MatrixLoader = ({ size = 'md', className }: Props) => {
  const { t } = useTranslation();

  return (
    <span
      role="status"
      aria-label={t('description.loading')}
      data-slot="matrix-loader"
      className={classNames('matrix-loader', size === 'sm' && 'matrix-loader--sm', className)}
    >
      {dots.map((dot) =>
        dot.role === 'ring' ? (
          <i key={dot.index} style={ringDelay(dot.step)} />
        ) : (
          <i key={dot.index} data-dot={dot.role} />
        )
      )}
    </span>
  );
};

export default MatrixLoader;
